import { useState } from 'react';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { CardActionArea } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';

import Notification from '../common/Notification';
import { RootState } from '../../store/store';
import { useAppSelector, useAppDispatch } from '../../store/hooks/useTypedSelector';
import { updateCart } from '../../store/slices/cartSlice';
import { product } from '../../types';
import { COLORS } from '../../utils/ColorsTheme';
import './_product.scss';

const Product = () => {
  const products = useAppSelector((state: RootState) => state.product.filteredProducts);
  const noFilteredProducts = useAppSelector((state: RootState) => state.product.noFilteredProducts);
  const cart = useAppSelector((state: RootState) => state.cart);
  const [showMessage, setMessage] = useState<string>('');
  const [isSuccess, setSuccess] = useState<boolean>(false);
  const skeletonArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const addProductToCart = (e: React.MouseEvent<HTMLAnchorElement>, product: product) => {
    e.preventDefault();
    e.stopPropagation();
    if (cart.products.length) {
      const productAlreadyInCart = cart.products.find((prod) => prod.id === product.id);
      if (productAlreadyInCart?.quantity) {
        const totalQuantity = productAlreadyInCart.quantity + 1;
        if (totalQuantity > 10) {
          setMessage('');
          setTimeout(() => {
            setMessage('User can add maximum of 10 item of same products in the cart');
          }, 100);
          setSuccess(false);
          return;
        }
      }
    }
    const productData = { ...product, quantity: 1, productTotal: 1 };
    dispatch(updateCart(productData));
    setMessage('');
    setTimeout(() => {
      setMessage('Product Added to Cart successfully!');
    }, 100);
    setSuccess(true);
  };
  const cardClickHandler = (product: product) => {
    navigate(`/product-details/${product.id}`, { state: product });
  };
  const handleClose = () => {
    setMessage('');
    setSuccess(false);
  };

  return (
    <>
      <div className='row'>
        {products.map((item, index) => (
          <div className='col-lg-3 col-md-4' key={index}>
            <Card
              sx={{ width: 320 }}
              onClick={() => cardClickHandler(item)}
              className='product__item'
              data-testid={`product-id-${item.id}`}
              style={{
                backgroundColor:
                  theme.palette.mode === 'dark' ? COLORS.defaultDarkMode : COLORS.defaultLightMode,
              }}
            >
              <CardActionArea>
                <CardMedia component='img' height='360' image={item.imageSrc} alt={item.name} />
                <Chip
                  label={`₹ ${parseFloat(item.price).toFixed(2)}`}
                  className='position-absolute price-label'
                  color='primary'
                  clickable
                />
                <CardContent>
                  <Typography gutterBottom variant='h5' component='div' className='text-truncate'>
                    {item.name}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' className='text-truncate'>
                    {item.description}
                  </Typography>
                  <ul className='product__hover'>
                    <li>
                      <a href='#' onClick={(e) => addProductToCart(e, item)} data-testid={`add-product-${item.id}-to-cart`}>
                        <AddShoppingCartIcon />
                      </a>
                    </li>
                  </ul>
                </CardContent>
              </CardActionArea>
            </Card>
          </div>
        ))}
        {!products.length &&
          !noFilteredProducts &&
          skeletonArray.map((item, index) => (
            <div className='col-lg-3 col-md-4' key={index}>
              <Skeleton variant='rounded' width={320} height={453} className='product__item' />
            </div>
          ))}
        {!products.length && noFilteredProducts && (
          <Typography
            gutterBottom
            variant='h5'
            component='div'
            className='d-flex justify-content-center'
          >
            Products coming soon.
          </Typography>
        )}
      </div>
      {showMessage !== '' && (
        <Notification showMessage={showMessage} handleClose={handleClose} isSuccess={isSuccess} />
      )}
    </>
  );
};

export default Product;
